import { Link } from "react-router-dom";
import { ArrowRight, FolderKanban, FileSpreadsheet, Braces, Send, History, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { icon: FolderKanban, title: "Five isolated projects", text: "Each project keeps its own leads, templates, sender and history." },
  { icon: FileSpreadsheet, title: "CSV & Excel import", text: "Drop a .csv, .xlsx or .xls list. Emails are detected and de-duplicated." },
  { icon: Braces, title: "Merge tags", text: "Personalise with {name}, {company}, {title} in subject and body." },
  { icon: Send, title: "Resend delivery", text: "Bulk campaigns dispatched in batches through one Resend account." },
  { icon: History, title: "Send history", text: "Every recipient, status and Resend ID logged per project." },
  { icon: ShieldCheck, title: "Unsubscribe built in", text: "Every email carries a one-click unsubscribe link and header." },
];

function Nav() {
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="h-7 w-7 rounded-md bg-primary grid place-items-center font-display font-bold text-sm">R</div>
          <span className="font-display font-semibold tracking-tight">Sales Leads Redoc</span>
        </div>
        <Button asChild size="sm" className="rounded-full px-5" data-testid="landing-login-button">
          <Link to="/admin">Admin login</Link>
        </Button>
      </div>
    </header>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen noise">
      <Nav />
      <main className="grid-bg">
        <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <p className="rise text-xs font-mono uppercase tracking-[0.2em] text-primary mb-6">Email automation · Resend</p>
            <h1 className="rise d1 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
              Upload a list.<br />Write once.<br /><span className="text-primary">Reach every lead.</span>
            </h1>
            <p className="rise d2 mt-8 text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed">
              Sales Leads Redoc is a focused outreach console: five independent projects, spreadsheet lead import,
              personalised templates and bulk sending through a single Resend account.
            </p>
            <div className="rise d3 mt-10 flex items-center gap-4">
              <Button asChild size="lg" className="rounded-full px-7 group" data-testid="landing-cta-admin">
                <Link to="/admin">Open admin console <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              </Button>
              <span className="text-xs font-mono text-muted-foreground">/admin</span>
            </div>
          </div>
          <div className="lg:col-span-6 rise d4">
            <div className="relative rounded-2xl border border-border bg-card p-2 shadow-2xl shadow-stone-400/30">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
                alt="Dashboard preview" className="rounded-xl w-full h-[340px] object-cover opacity-95"
              />
              <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-xl p-4 w-60 hidden sm:block">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Project 3 · Campaign</p>
                <p className="font-display text-2xl font-bold mt-1">248 <span className="text-sm text-emerald-700 font-mono">sent</span></p>
                <div className="h-1.5 bg-secondary rounded-full mt-3 overflow-hidden"><div className="h-full w-4/5 bg-primary" /></div>
              </div>
            </div>
          </div>
        </section>
        <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-28">
          <h2 className="text-base md:text-lg font-semibold mb-8 text-muted-foreground">What's inside</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden border border-border">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-card p-8 hover:bg-secondary/60 transition-colors">
                <f.icon className="h-5 w-5 text-primary mb-5" />
                <h3 className="font-display text-lg font-semibold">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground font-mono">
        © {new Date().getFullYear()} Logardt Group
      </footer>
    </div>
  );
}
